/**
 * Audio timing — §5.3. All pure, so the entire playback plan is testable at a fixed
 * instant with no clock faking and no browser.
 */
import { parseVerseKey } from './navigation'
import type { ReciterMeta, SurahAudio } from './types'

/**
 * `t` holds BOUNDARIES, not durations: ayah n spans t[n-1] … t[n], so the array is
 * always versesCount + 1 long.
 */
export function ayahStartMs(audio: SurahAudio | undefined, ayah: number): number | null {
  return audio?.t[ayah - 1] ?? null
}

export function ayahEndMs(audio: SurahAudio | undefined, ayah: number): number | null {
  return audio?.t[ayah] ?? null
}

/**
 * Binary search over the boundaries, returning a 1-based ayah.
 *
 * MUST clamp: polling can fire a few ms past the final boundary, and reporting
 * "ayah 0" there clears the highlight on the last ayah of every surah.
 */
export function ayahAtMs(audio: SurahAudio | undefined, ms: number): number | null {
  const t = audio?.t
  if (!t || t.length < 2) return null
  const lastAyah = t.length - 1
  if (ms <= t[0]!) return 1
  if (ms >= t[lastAyah]!) return lastAyah

  let lo = 0
  let hi = lastAyah
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1
    if (t[mid]! <= ms) lo = mid
    else hi = mid
  }
  return Math.min(lastAyah, Math.max(1, lo + 1))
}

export interface SurahRun {
  surah: number
  /** Index into the queue of the run's first key. */
  from: number
  /** Index into the queue of the run's last key. */
  to: number
}

/**
 * The contiguous per-surah stretches inside a queue.
 *
 * A run is exactly "how far the currently loaded file can carry us". A page usually
 * sits in one surah but can straddle a boundary, and each surah is a separate audio
 * file, so a straddling page plays two files, switching where a pause already belongs
 * musically.
 */
export function surahRuns(queue: readonly string[]): SurahRun[] {
  const runs: SurahRun[] = []
  for (let i = 0; i < queue.length; i += 1) {
    const ref = parseVerseKey(queue[i]!)
    if (!ref) continue
    const last = runs[runs.length - 1]
    if (last && last.surah === ref.surah && last.to === i - 1) last.to = i
    else runs.push({ surah: ref.surah, from: i, to: i })
  }
  return runs
}

/** The run containing a queue index, or null. */
export function runAt(runs: readonly SurahRun[], index: number): SurahRun | null {
  return runs.find((r) => index >= r.from && index <= r.to) ?? null
}

// ---------------------------------------------------------------------------
// Reciter grouping (§3.6)
// ---------------------------------------------------------------------------

/**
 * Style is really TEMPO, and the spread is enormous: al-Fātiḥa takes 34 s from Sudais
 * and 78 s from ʿAbd al-Bāsiṭ mujawwad.
 *
 * Group by style PREFIX, not equality — one reciter's style carries a variant suffix
 * («مرتّل (تسجيل آخر)»), and matching by equality puts them in a group of their own.
 */
export const RECITER_STYLE_PREFIXES = ['مرتّل', 'معلّم', 'مجوّد'] as const

export function reciterStyleGroup(reciter: ReciterMeta): string {
  const found = RECITER_STYLE_PREFIXES.find((p) => reciter.style.startsWith(p))
  return found ?? reciter.style
}

export interface ReciterGroup {
  style: string
  reciters: ReciterMeta[]
}

/** Grouped by style prefix, sorted within a group by measured pace. */
export function groupReciters(reciters: readonly ReciterMeta[]): ReciterGroup[] {
  const groups = new Map<string, ReciterMeta[]>()
  for (const r of reciters) {
    const style = reciterStyleGroup(r)
    const bucket = groups.get(style)
    if (bucket) bucket.push(r)
    else groups.set(style, [r])
  }

  const order = (style: string) => {
    const at = (RECITER_STYLE_PREFIXES as readonly string[]).indexOf(style)
    return at === -1 ? RECITER_STYLE_PREFIXES.length : at
  }

  return [...groups.entries()]
    .sort(([a], [b]) => order(a) - order(b))
    .map(([style, list]) => ({ style, reciters: [...list].sort((a, b) => a.pace - b.pace) }))
}
